// src/application-steps/StepContent1.js
import React, { memo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";

import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormSelect from "../../helpers/FormSelect";
import FormRadioGroup from "../../helpers/FormRadioGroup";
import FormCheckbox from "../../helpers/FormCheckbox";

function StepContent1({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
}) {
  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) {
      return "error";
    }
    if (isRequired && formData[field]) {
      return "success";
    }
    if (!isRequired && !formData[field]) {
      return "warning";
    }
    return "";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="form-group"
    >
      <form className="form-group">
        {/* Header */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={800} textAlign="center">
            Personal Information:
          </Typography>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Name */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={4}>
            <FormInput
              label="First Name"
              id="firstName"
              value={formData?.firstName}
              onChange={handleChange("firstName")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.firstName)}
              helperText={stepErrors.firstName || ""}
              status={getStatus("firstName", true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Middle Name"
              id="middleName"
              value={formData?.middleName}
              onChange={handleChange("middleName")}
              disabled={isReadOnly}
              status={getStatus("middleName", false)}
              helperText={stepErrors.middleName || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Last Name"
              id="lastName"
              value={formData?.lastName}
              onChange={handleChange("lastName")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.lastName)}
              helperText={stepErrors.lastName || ""}
              status={getStatus("lastName", true)}
            />
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* Gender */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={12}>
            <FormRadioGroup
              label="Gender"
              name="gender"
              value={formData?.gender}
              onChange={handleChange("gender")}
              options={[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
                { value: "Non-binary", label: "Non-binary" },
                { value: "self-describe", label: "Prefer to self-describe" },
                { value: "prefer-not-to-say", label: "Prefer not to say" },
              ]}
              row
              required
              isReadOnly={isReadOnly}
              error={Boolean(stepErrors.gender)}
              helperText={stepErrors.gender || ""}
              status={getStatus("gender", true)}
            />
          </Grid>
        </FormRow>

        {formData?.gender === "self-describe" && (
          <FormRow spacing={2}>
            <Grid item xs={12} md={6}>
              <FormInput
                label="Please describe your gender"
                id="genderOther"
                value={formData?.genderOther || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    genderOther: e.target.value,
                  }))
                }
                required
                disabled={isReadOnly}
                error={Boolean(stepErrors.genderOther)}
                helperText={stepErrors.genderOther || ""}
                status={getStatus("genderOther", true)}
              />
            </Grid>
          </FormRow>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Indigenous */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormRadioGroup
              label="Do you identify as Indigenous?"
              name="indigenous"
              value={formData?.indigenous}
              onChange={handleChange("indigenous")}
              options={[
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
                { value: "Prefer not to say", label: "Prefer not to say" },
              ]}
              row
              required
              isReadOnly={isReadOnly}
              error={Boolean(stepErrors.indigenous)}
              helperText={stepErrors.indigenous || ""}
              status={getStatus("indigenous", true)}
            />
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* Citizenship + SIN */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormRadioGroup
              label="Citizenship"
              name="citizenship"
              value={formData?.citizenship}
              onChange={handleChange("citizenship")}
              options={[
                { value: "Canadian Citizen", label: "Canadian Citizen" },
                { value: "Permanent Resident", label: "Permanent Resident" },
              ]}
              row
              isReadOnly={isReadOnly}
              error={Boolean(stepErrors.citizenship)}
              helperText={stepErrors.citizenship || ""}
              status={getStatus("citizenship", true)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              label="SIN Number"
              id="sin"
              value={formData?.SIN}
              onChange={handleChange("SIN")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.SIN)}
              helperText={stepErrors.SIN || ""}
              status={getStatus("SIN", true)}
            />
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* School + CTC */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormSelect
              label="School"
              id="school"
              value={formData?.school_id}
              onChange={handleChange("school_id")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.school_id)}
              helperText={stepErrors.school_id || ""}
              status={getStatus("school_id", true)}
            >
              <MenuItem value="">
                <em>Select School</em>
              </MenuItem>
              <MenuItem value="1">Alternate</MenuItem>
              <MenuItem value="12">Cedar</MenuItem>
              <MenuItem value="4">Dover Bay Secondary</MenuItem>
              <MenuItem value="5">IslandConnectED</MenuItem>
              <MenuItem value="2">John Barsby Community Secondary</MenuItem>
              <MenuItem value="6">Ladysmith Secondary</MenuItem>
              <MenuItem value="7">Nanaimo District Secondary</MenuItem>
              <MenuItem value="9">Wellington Secondary</MenuItem>
            </FormSelect>
          </Grid>

          <Grid item xs={12} md={12}>
            <FormCheckbox
              label="Check if you are in the CTC program:"
              checked={formData?.CTC}
              onChange={handleChange("CTC")}
              name="ctc"
              status=""
              displaySide="left"
              isReadOnly={isReadOnly}
            />
          </Grid>
        </FormRow>

        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          <Typography variant="body2">
            * Applicants registered in a IslandConnectED course that is
            necessary for graduation must have completed 60% of the course and
            have a passing grade. The marks must be submitted to the schools by
            the April reporting period. This also includes all Distance Learning
            courses.
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* Address */}
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={800}>
            Address Details:
          </Typography>
        </Stack>

        <FormRow spacing={2}>
          <Grid item xs={12} md={8}>
            <FormInput
              label="Mailing Address (House # and Street Name)"
              id="address"
              value={formData?.address}
              onChange={handleChange("address")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.address)}
              helperText={stepErrors.address || ""}
              status={getStatus("address", true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInput
              label="City"
              id="city"
              value={formData?.city}
              onChange={handleChange("city")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.city)}
              helperText={stepErrors.city || ""}
              status={getStatus("city", true)}
            />
          </Grid>
        </FormRow>

        <FormRow spacing={2}>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Province"
              id="province"
              value={formData?.province}
              onChange={handleChange("province")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.province)}
              helperText={stepErrors.province || ""}
              status={getStatus("province", true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Postal Code"
              id="postalCode"
              value={formData?.postalCode}
              onChange={handleChange("postalCode")}
              required
              pattern="^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} \d{1}[A-Z]{1}\d{1}$"
              title="Invalid Format: A#A #A# (Caps)"
              disabled={isReadOnly}
              error={Boolean(stepErrors.postalCode)}
              helperText={stepErrors.postalCode || ""}
              status={getStatus("postalCode", true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormSelect
              label="Country"
              id="country"
              value={formData?.country}
              onChange={handleChange("country")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.country)}
              helperText={stepErrors.country || ""}
              status={getStatus("country", true)}
            >
              <MenuItem value="Canada">Canada</MenuItem>
              <MenuItem value="Afghanistan">Afghanistan</MenuItem>
              <MenuItem value="Albania">Albania</MenuItem>
              {/* Add all other countries as needed */}
            </FormSelect>
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* Phones */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Cell Phone"
              id="cellPhone"
              value={formData?.cellPhone}
              onChange={handleChange("cellPhone")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.cellPhone)}
              helperText={stepErrors.cellPhone || ""}
              status={getStatus("cellPhone", true)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Home Phone"
              id="homePhone"
              value={formData?.homePhone}
              onChange={handleChange("homePhone")}
              error={Boolean(stepErrors.homePhone)}
              helperText={stepErrors.homePhone || ""}
              disabled={isReadOnly}
              status={getStatus("homePhone", false)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormInput
              label="Work Phone"
              id="workPhone"
              value={formData?.workPhone}
              onChange={handleChange("workPhone")}
              error={Boolean(stepErrors.workPhone)}
              helperText={stepErrors.workPhone || ""}
              disabled={isReadOnly}
              status={getStatus("workPhone", false)}
            />
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* DOB + PEN */}
        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <FormInput
              label="Date of Birth"
              id="dob"
              type="date"
              value={formData?.dob}
              onChange={handleChange("dob")}
              required
              disabled={isReadOnly}
              error={Boolean(stepErrors.dob)}
              helperText={stepErrors.dob || ""}
              status={getStatus("dob", true)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: new Date(
                  new Date().getFullYear() - 15,
                  new Date().getMonth(),
                  new Date().getDate()
                )
                  .toISOString()
                  .slice(0, 10),
                min: new Date(
                  new Date().getFullYear() - 21,
                  new Date().getMonth(),
                  new Date().getDate()
                )
                  .toISOString()
                  .slice(0, 10),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormInput
              label="SD68 Student PEN Number: (See Report Card)"
              id="pen"
              value={formData?.PEN}
              onChange={handleChange("PEN")}
              required
              error={Boolean(stepErrors.PEN)}
              helperText={stepErrors.PEN || ""}
              disabled={isReadOnly}
              status={getStatus("PEN", true)}
            />
          </Grid>
        </FormRow>
      </form>
    </Paper>
  );
}

export default memo(StepContent1);
